import React, { useEffect, useState, useContext } from "react";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import PhotoUpload from "./PhotoUpload";
import { getProjectPhotos, deleteProjectPhoto, addProjectPhoto, markPhotoForInvoice, shareProjectAsset } from "api/Projects";
import ProviderShareSelect from "components/ui/ProviderShareSelect";
import { getPresignedUrl } from "api/Media";
import moment from "moment-timezone";
import { TrashIcon, QuestionMarkIcon } from "components/svg";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import Checkbox from "components/forms/Checkbox";
import P from "components/typography/P";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";

const Photos = ({ isCompany, project, isProvider }: { isCompany: boolean; project: any; isProvider: any }) => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [confirmStepModalOpen, setConfirmStepModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dropInputRef = React.useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  useEffect(() => {
    _init();
  }, []);

  const _init = async () => {
    const res = await getProjectPhotos({ data: project });
    if (res?.length) {
      setPhotos(res);
    } else {
      setPhotos([]);
    }
  };

  const handleDirectUpload = async (file: File) => {
    if (!file || !project?.id) return;
    setUploading(true);
    try {
      const signed = await getPresignedUrl({ data: { directory: 'projects/' + project.id + '/photos', name: file.name, type: file.type, size: file.size } });
      if (signed?.success && signed?.url) {
        const putRes = await fetch(signed.url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (putRes.ok) {
          const reg = await addProjectPhoto({ id: project.id, data: { url: signed.location } });
          if (reg?.id) {
            void trackActivity({ module: 'projects', action: 'upload_project_photo', actionType: 'create', userId: user?.id || null, companyId: user?.company?.id || null, entityType: 'project', entityId: project.id, metadata: { photoId: reg.id }, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null });
            toast.add('Photo uploaded.', 'var(--theme-ui-colors-success)');
            _init();
          } else { toast.add('Error registering photo.', 'var(--theme-ui-colors-danger)'); }
        } else { toast.add('Error uploading photo.', 'var(--theme-ui-colors-danger)'); }
      } else { toast.add('Error getting upload URL.', 'var(--theme-ui-colors-danger)'); }
    } catch (e) { toast.add('Upload failed.', 'var(--theme-ui-colors-danger)'); }
    setUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); const file = e.dataTransfer.files?.[0]; if (file) handleDirectUpload(file); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDropInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) handleDirectUpload(file); e.target.value = ''; };

  const _cancelConfirmModal = () => {
    setConfirmStepModalOpen(false);
  };

  const _deletePhoto = async (photo: any) => {
    setSubmitting(true);
    const res = await deleteProjectPhoto({ data: { id: project.id, photoId: photo.id } });
    if (res.success) {
      void trackActivity({
        module: "projects",
        action: "delete_project_photo",
        actionType: "delete",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "project",
        entityId: project.id,
        metadata: {
          photoId: photo.id,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setSubmitting(false);
      setConfirmStepModalOpen(false);
      toast.add(`Photo deleted.`, "var(--theme-ui-colors-success)");
      _init();
    } else {
      toast.add(`Error deleting photo.`, "var(--theme-ui-colors-danger)");
    }
  };

  const _openConfirmModal = (photo: any) => {
    setSelectedPhoto(photo);
    setConfirmStepModalOpen(true);
  };

  // Session 65 item 10 - restore the per-photo "Include on Invoice"
  // control on the standalone project Photos tab. Non-maintenance
  // projects rely on this here (production shows it); the invoice-side
  // ProjectPhotoIncludePicker (used by the WO invoice tab and the invoice
  // Overview) is UNCHANGED and writes the SAME `invoiceInclude` flag via
  // the SAME `markPhotoForInvoice` endpoint, so the two surfaces stay in
  // sync. Optimistic local update, revert on failure.
  const _togglePhotoInvoice = async (photo: any) => {
    const newValue = !photo?.invoiceInclude;
    setPhotos((prev) =>
      prev.map((p) => (p.id === photo.id ? { ...p, invoiceInclude: newValue } : p)),
    );
    const res = await markPhotoForInvoice({
      id: project.id,
      data: { include: newValue, photoId: photo.id },
    });
    if (!res?.success) {
      // Revert the optimistic flip and tell the user. Matches the
      // ProjectPhotoIncludePicker's success check (`res?.success`).
      setPhotos((prev) =>
        prev.map((p) => (p.id === photo.id ? { ...p, invoiceInclude: !newValue } : p)),
      );
      toast.add("Could not update this photo. Try again.", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Photos
      </H5>
      {photos?.length > 0 && (isCompany || isProvider) && (
        <Flex sx={{ justifyContent: "flex-start", mb: 2 }}>
          <PhotoUpload title={"Add Photo"} project={project} afterUpload={_init} />
        </Flex>
      )}
      {photos?.length === 0 && (
        <Flex
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && dropInputRef.current?.click()}
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            border: "2px dashed",
            borderColor: dragActive ? "primary" : "inputOutline",
            backgroundColor: dragActive ? "rgba(0,0,0,0.02)" : "transparent",
            borderRadius: "12px",
            padding: "40px",
            mt: 2,
            cursor: uploading ? "wait" : "pointer",
            transition: "border-color 0.15s, background-color 0.15s",
          }}>
          <Box sx={{ width: 48, height: 48, mb: 3, opacity: 0.3 }}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px" fontWeight={500} marginTop="0px" marginBottom="4px">
            No photos yet
          </P>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px" fontWeight={400} marginTop="0px" marginBottom="16px" style={{ opacity: 0.7 }}>
            {uploading ? "Uploading..." : "Drag and drop or click to browse"}
          </P>
          {uploading && <Spinner size={20} strokeWidth={3} />}
          <input ref={dropInputRef} type="file" accept="image/*" hidden onChange={handleDropInputChange} />
        </Flex>
      )}
      <Grid
        gap={3}
        sx={{
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          mt: 3,
        }}
      >
        {photos.map((photo, idx) => (
          <Flex
            key={idx}
            sx={{
              maxWidth: "600px",
              width: "100%",
              flexDirection: "column",
              borderRadius: "6px",
              border: `1px solid ${themevals.colors.inputOutline}`,
              boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
              padding: "15px 10px",
            }}
          >
            <Box sx={{ width: "100%", padding: "0px 5px" }}>
              <Label sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, marginBottom: "10px" }}>
                {isProvider || !isCompany
                  ? `Uploaded on ${moment(photo?.createdAt).format("MMM D, YYYY")}`
                  : `Uploaded by ${photo?.uploadedBy?.firstName} ${photo?.uploadedBy?.lastName} on ${moment(photo?.createdAt).format("MMM D, YYYY")}`}
              </Label>
            </Box>
            <Box
              sx={{
                width: "100%",
                paddingTop: "66.66%", // 3:2 aspect ratio
                backgroundImage: `url(${photo.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "6px",
                marginBottom: "15px",
              }}
            />
            {(isCompany || isProvider) && (
              <Flex sx={{ alignItems: "center" }}>
                {/* Session 65 item 10 - per-photo "Include on Invoice"
                    restored on the standalone project Photos tab. Company
                    only (a provider can see photos but does not control
                    the client invoice); writes the shared `invoiceInclude`
                    flag, in sync with the invoice-side picker. */}
                <Flex sx={{ flex: 1 }}>
                  {isCompany && (
                    <Box sx={{ marginRight: "12px" }}>
                      <ProviderShareSelect
                        projectId={project.id}
                        value={photo?.sharedWithProviderId}
                        onChange={async (providerId) => {
                          const res = await shareProjectAsset({ projectId: project.id, kind: "photos", assetId: photo.id, providerId });
                          if (res?.success) _init();
                          else toast.add(res?.message || "Could not change sharing.", "var(--theme-ui-colors-danger)");
                        }}
                      />
                    </Box>
                  )}
                  {isCompany && (
                    <Checkbox
                      label="Include on Invoice"
                      checked={!!photo?.invoiceInclude}
                      onChange={() => _togglePhotoInvoice(photo)}
                      labelSize="12px"
                    />
                  )}
                </Flex>
                <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                  {user.id === photo?.uploadedBy?.id && (
                    <Box sx={{ cursor: "pointer" }} onClick={() => _openConfirmModal(photo)}>
                      <TrashIcon fill={themevals.colors.dkgrey} width={"14px"} />
                    </Box>
                  )}
                </Flex>
              </Flex>
            )}
          </Flex>
        ))}
      </Grid>
      {confirmStepModalOpen && (
        <BasicModal
          title={`Delete Photo?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _deletePhoto(selectedPhoto)}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Delete Photo"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to delete the photo from the project?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Photos;
